import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Spinner } from '../../components/ui/spinner';
import { fmtBRL, fmtCompetencia } from '../../lib/format';
import { AlertTriangle, Search, UserPlus } from 'lucide-react';

interface CpfSemMatch {
  cpf: string;
  nome: string;
  matricula: string | null;
  ocorrencias: number;
  competencias: string[];
  ultimaCompetencia: string;
  valorMedio: number;
  statusEtipiPredominante: string;
  cargoMaisFrequente: string | null;
  orgaoPagto: string | null;
}

function mascararCpf(c: string): string {
  return c.length === 11 ? `${c.slice(0,3)}.${c.slice(3,6)}.${c.slice(6,9)}-${c.slice(9)}` : c;
}

const statusDescricao: Record<string, string> = {
  '1': 'efetivado',
  '2': 'sem margem',
  '3': 'não localizado',
  '4': 'valor divergente',
  '5': 'problema técnico',
  '6': 'erro',
  'S': 'compra dívida / SEAD',
};

export function SemMatchPage() {
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['sem-match', { q: search }],
    queryFn: () => {
      const params = new URLSearchParams({ size: '100' });
      if (search) params.set('q', search);
      return api<{ items: CpfSemMatch[]; total: number }>(
        `/importacao/sem-match/cpfs?${params.toString()}`,
      );
    },
  });

  function paraCadastrar(item: CpfSemMatch): string {
    // Monta query string pra pré-preencher o novo cadastro
    const params = new URLSearchParams({
      cpf: item.cpf,
      nome: item.nome,
    });
    if (item.matricula) params.set('matricula', item.matricula);
    return `/associados/novo?${params.toString()}`;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-3">
          CPFs sem associado
          <AlertTriangle className="h-6 w-6 text-amber-500" />
        </h1>
        <p className="text-muted-foreground">
          {data?.total ?? 0} CPFs vieram nas importações ETIPI mas ainda não têm associado cadastrado
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex gap-2 items-center">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome, CPF ou matrícula…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') setSearch(q); }}
              className="flex-1"
            />
            <Button variant="secondary" onClick={() => setSearch(q)}>Buscar</Button>
            {search && <Button variant="ghost" size="sm" onClick={() => { setQ(''); setSearch(''); }}>limpar</Button>}
          </div>
          <CardDescription className="mt-2">
            Ordenados pela frequência de aparições. CPFs em 3/3 importações são provavelmente associados ativos.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading && <Spinner />}
          {!isLoading && data && data.items.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">
              {search ? 'Nenhum resultado.' : 'Todos os CPFs já têm associado cadastrado.'}
            </p>
          )}
          {!isLoading && data && data.items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground border-b">
                  <tr>
                    <th className="text-left py-2 pr-3">Nome</th>
                    <th className="text-left py-2 pr-3">CPF</th>
                    <th className="text-left py-2 pr-3">Matrícula</th>
                    <th className="text-center py-2 pr-3">Ocorrências</th>
                    <th className="text-left py-2 pr-3">Última</th>
                    <th className="text-right py-2 pr-3">Valor médio</th>
                    <th className="text-left py-2 pr-3">Status</th>
                    <th className="text-right py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((it) => {
                    const statusDesc = statusDescricao[it.statusEtipiPredominante] ?? '?';
                    const isFreq = it.ocorrencias >= 3;
                    return (
                      <tr key={it.cpf} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="py-2 pr-3 font-medium">{it.nome}</td>
                        <td className="py-2 pr-3 font-mono text-xs">{mascararCpf(it.cpf)}</td>
                        <td className="py-2 pr-3 font-mono text-xs">{it.matricula ?? '—'}</td>
                        <td className="py-2 pr-3 text-center">
                          <span className={`text-xs px-2 py-0.5 rounded ${isFreq ? 'bg-blue-100 text-blue-900' : 'bg-gray-100'}`}>
                            {it.ocorrencias}
                          </span>
                        </td>
                        <td className="py-2 pr-3 text-xs">{fmtCompetencia(it.ultimaCompetencia)}</td>
                        <td className="py-2 pr-3 text-right">{fmtBRL(it.valorMedio)}</td>
                        <td className="py-2 pr-3">
                          <span className="text-xs px-1.5 py-0.5 rounded bg-gray-50 font-mono">
                            {it.statusEtipiPredominante}
                          </span>
                          <span className="text-xs text-muted-foreground ml-1">{statusDesc}</span>
                        </td>
                        <td className="py-2 text-right">
                          <Link to={paraCadastrar(it)}>
                            <Button size="sm" variant="secondary">
                              <UserPlus className="h-3 w-3 mr-1" />Cadastrar
                            </Button>
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {data.total > data.items.length && (
                <p className="text-xs text-muted-foreground mt-3 text-center">
                  Exibindo {data.items.length} de {data.total} CPFs. Use a busca pra filtrar.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
